/**
 * 교회별 교사 — 교회 등록번호로 묶은 아코디언 → 교사 명단 → 교사 상세(Drawer)
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, InputAdornment, LinearProgress, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ChevronRight } from '@mui/icons-material';
import { api } from '../api';
import { C, CLUBS, downloadBlob, toCsv } from '../common';
import { ClubChips, Empty, ToneChip } from './ui';
import TeacherDrawer from './TeacherDrawer';

export type RosterTeacher = {
  _id: string; personKey: string; regNo: string; churchName: string; teacherName: string; teacherNameEn: string;
  teacherPhone: string; keyCode: string; clubs: string[]; position: string; awanaRole: string; status: string;
  registrations: number; education: Array<{ code: string; year: number; result: string }>;
  certificates: Array<{ serial: string; club: string; completedAt: string }>;
  certStatus: 'issued' | 'pending' | 'none'; issues: string[];
};

const ISSUE_FILTER: Record<string, (t: RosterTeacher) => boolean> = {
  noClub: (t) => !t.clubs.length,
  noEn: (t) => !t.teacherNameEn,
  dup: (t) => t.registrations > 1,
  phone: (t) => t.issues.includes('전화번호 확인'),
};

const CertCell: React.FC<{ t: RosterTeacher }> = ({ t }) => (
  t.certStatus === 'issued'
    ? <ToneChip tone="ok" label={`발급 ${t.certificates.map((c) => c.serial).join(', ')}`} />
    : t.certStatus === 'pending' ? <ToneChip tone="warn" label="보고서 확인 중" /> : <ToneChip tone="gray" dashed label="미발급" />
);

const ChurchTeachersTab: React.FC<{ initialIssue?: string }> = ({ initialIssue }) => {
  const [list, setList] = useState<RosterTeacher[] | null>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<'church' | 'all'>('church');
  const [q, setQ] = useState('');
  const [club, setClub] = useState('');
  const [cert, setCert] = useState('');
  const [issue, setIssue] = useState(initialIssue || '');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<string | null>(null);

  const load = () => api<{ data: RosterTeacher[] }>('/admin/edu/teachers').then((r) => setList(r.data)).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (initialIssue) { setIssue(initialIssue); setView('all'); } }, [initialIssue]);

  const filtered = useMemo(() => {
    if (!list) return [];
    const kw = q.trim().toLowerCase().replace(/-/g, '');
    return list.filter((t) => {
      if (club && !t.clubs.includes(club)) return false;
      if (cert && t.certStatus !== cert) return false;
      if (issue && !(ISSUE_FILTER[issue]?.(t))) return false;
      if (!kw) return true;
      return [t.churchName, t.regNo, t.teacherName, t.teacherNameEn, t.teacherPhone.replace(/-/g, ''), t.keyCode]
        .some((v) => String(v || '').toLowerCase().replace(/-/g, '').includes(kw));
    });
  }, [list, q, club, cert, issue]);

  const churches = useMemo(() => {
    const map = new Map<string, { key: string; regNo: string; churchName: string; teachers: RosterTeacher[] }>();
    filtered.forEach((t) => {
      const key = t.regNo || `name:${t.churchName}`;
      const g = map.get(key) || { key, regNo: t.regNo, churchName: t.churchName, teachers: [] };
      g.teachers.push(t);
      map.set(key, g);
    });
    return Array.from(map.values()).sort((a, b) => (a.regNo || '9999').localeCompare(b.regNo || '9999') || a.churchName.localeCompare(b.churchName, 'ko'));
  }, [filtered]);

  const exportCsv = () => {
    const rows = [['등록번호', '교회', '이름', '영문 이름', '휴대폰', '교사번호', '클럽', '직분', '수료증', '교육 이력', '데이터 확인']];
    filtered.forEach((t) => rows.push([
      t.regNo, t.churchName, t.teacherName, t.teacherNameEn, t.teacherPhone, t.keyCode, t.clubs.join(' · '), t.position,
      t.certificates.map((c) => c.serial).join(' '), t.education.map((e) => `${e.year} ${e.code}`).join(', '), t.issues.join(', '),
    ]));
    downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }), `교사명단_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!list) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  const row = (t: RosterTeacher, showChurch: boolean) => (
    <TableRow key={t._id} hover onClick={() => setSelected(t._id)} sx={{ cursor: 'pointer' }}>
      {showChurch && <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography fontSize={13} fontWeight={700}>{t.churchName}</Typography><Typography fontSize={11.5} color={C.sub}>{t.regNo}</Typography></TableCell>}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography fontWeight={700} fontSize={14}>{t.teacherName}</Typography>
        <Typography fontSize={11.5} color={t.teacherNameEn ? C.sub : C.warn}>{t.teacherNameEn || '영문 이름 없음'}</Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{t.teacherPhone}</TableCell>
      <TableCell><ClubChips clubs={t.clubs} /></TableCell>
      <TableCell sx={{ fontSize: 13 }}>{[t.position, t.awanaRole].filter(Boolean).join(' · ')}</TableCell>
      <TableCell sx={{ fontSize: 12.5 }}>{t.education.length ? Array.from(new Set(t.education.map((e) => e.code))).join(' · ') : <span style={{ color: C.sub }}>-</span>}</TableCell>
      <TableCell><CertCell t={t} /></TableCell>
      <TableCell>{t.issues.filter((i) => i !== '영문 이름 없음' && i !== '클럽 없음').map((i) => <ToneChip key={i} tone="warn" label={i} sx={{ mr: 0.5 }} />)}</TableCell>
    </TableRow>
  );
  const head = (showChurch: boolean) => (
    <TableHead><TableRow sx={{ '& th': { fontSize: 12.5, color: C.sub, fontWeight: 700, bgcolor: '#fffbea' } }}>
      {showChurch && <TableCell>교회</TableCell>}<TableCell>이름</TableCell><TableCell>휴대폰</TableCell><TableCell>클럽</TableCell>
      <TableCell>직분·역할</TableCell><TableCell>교육 이력</TableCell><TableCell>BT 수료증</TableCell><TableCell>확인</TableCell>
    </TableRow></TableHead>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="church">교회별</ToggleButton>
          <ToggleButton value="all">교사 전체</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder="교회 · 등록번호 · 이름 · 휴대폰" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 260, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="클럽" value={club} onChange={(e) => setClub(e.target.value)} sx={{ width: 130 }}>
          <MenuItem value="">전체</MenuItem>{CLUBS.map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="수료 상태" value={cert} onChange={(e) => setCert(e.target.value)} sx={{ width: 130 }}>
          <MenuItem value="">전체</MenuItem><MenuItem value="issued">발급</MenuItem><MenuItem value="pending">확인 중</MenuItem><MenuItem value="none">미발급</MenuItem>
        </TextField>
        <TextField select size="small" label="데이터 확인" value={issue} onChange={(e) => setIssue(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">전체</MenuItem><MenuItem value="noClub">클럽 미지정</MenuItem><MenuItem value="noEn">영문 이름 없음</MenuItem>
          <MenuItem value="dup">중복 등록</MenuItem><MenuItem value="phone">휴대폰 확인</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={exportCsv}>CSV</Button>
      </Box>
      <Typography sx={{ fontSize: 13, color: C.sub, mb: 1 }}>교회 {churches.length}곳 · 교사 {filtered.length}명 (같은 이름+휴대폰은 1명으로 묶음)</Typography>

      {!filtered.length && <Empty>조건에 맞는 교사가 없습니다.</Empty>}

      {view === 'all' ? (
        <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 2 }}>
          <Table size="small">{head(true)}<TableBody>{filtered.slice(0, 1000).map((t) => row(t, true))}</TableBody></Table>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {churches.map((g) => {
            const issued = g.teachers.filter((t) => t.certStatus === 'issued').length;
            const pending = g.teachers.filter((t) => t.certStatus === 'pending').length;
            const issues = g.teachers.filter((t) => t.issues.length).length;
            const clubs = Array.from(new Set(g.teachers.flatMap((t) => t.clubs)));
            const isOpen = open[g.key] || (q.trim().length > 1 && churches.length <= 3);
            return (
              <Box key={g.key} sx={{ border: `1px solid ${C.line}`, borderRadius: 2.5, bgcolor: '#fff', overflow: 'hidden' }}>
                <Box onClick={() => setOpen({ ...open, [g.key]: !isOpen })}
                  sx={{ display: 'grid', gridTemplateColumns: { xs: '24px 1fr', md: '24px 64px 1.4fr 1.4fr 150px auto' }, gap: 1.5, alignItems: 'center', px: 1.5, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: '#fffbea' } }}>
                  {isOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: C.sub, display: { xs: 'none', md: 'block' } }}>{g.regNo || '----'}</Typography>
                  <Box>
                    <Typography fontWeight={800}>{g.churchName || '(교회명 없음)'}</Typography>
                    <Typography sx={{ fontSize: 12, color: C.sub, display: { md: 'none' } }}>{g.regNo} · 교사 {g.teachers.length}명</Typography>
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}><ClubChips clubs={clubs} /></Box>
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Typography sx={{ fontSize: 12, color: C.sub }}>교사 {g.teachers.length}명 · 발급 {issued}</Typography>
                    <LinearProgress variant="determinate" value={g.teachers.length ? (issued / g.teachers.length) * 100 : 0}
                      sx={{ height: 5, borderRadius: 3, bgcolor: '#f4eed6', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#ffe066,#d99a00)' } }} />
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, justifyContent: 'flex-end' }}>
                    {pending > 0 && <ToneChip tone="warn" label={`확인 대기 ${pending}`} />}
                    {issues > 0 && <Chip size="small" label={`확인 필요 ${issues}`} sx={{ height: 22, bgcolor: C.grayBg, color: C.gray }} />}
                  </Box>
                </Box>
                {isOpen && (
                  <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${C.line}` }}>
                    <Table size="small">{head(false)}<TableBody>{g.teachers.map((t) => row(t, false))}</TableBody></Table>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <TeacherDrawer teacherId={selected} onClose={() => setSelected(null)} onChanged={load} />
    </Box>
  );
};

export default ChurchTeachersTab;
